/**
 * Pure utility functions for Gantt chart time calculations.
 * No React dependencies — used by GanttChart, GanttTimeHeader, and GanttBar.
 */

export type TimeScale = 'day' | 'week' | 'month' | 'quarter';

export interface TimeInterval {
  date: Date;
  label: string;
  sublabel?: string;
}

export interface GanttTask {
  id: string;
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  duration?: number;
  progress?: number;
  dependencies: string[];
  group?: string;
  milestone: boolean;
  assignee?: string;
  color?: string;
  parentId?: string;
  iteration?: string;
}

/** Pixels per unit for each time scale */
export function getPixelsPerUnit(timeScale: TimeScale): number {
  switch (timeScale) {
    case 'day': return 40;
    case 'week': return 120;
    case 'month': return 200;
    case 'quarter': return 300;
  }
}

/** Calculate the visible time range from an array of tasks */
export function calculateTimeRange(tasks: GanttTask[]): { start: Date; end: Date } {
  const datedTasks = tasks.filter(t => t.startDate !== null);

  if (datedTasks.length === 0) {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 14);
    const end = new Date(today);
    end.setDate(end.getDate() + 14);
    return { start, end };
  }

  let earliest = Infinity;
  let latest = -Infinity;

  for (const task of datedTasks) {
    if (task.startDate) {
      const t = task.startDate.getTime();
      if (t < earliest) earliest = t;
      if (t > latest) latest = t;
    }
    if (task.endDate) {
      const t = task.endDate.getTime();
      if (t > latest) latest = t;
    } else if (task.startDate) {
      // No end date — assume start + 1 day
      const t = task.startDate.getTime() + 86400000;
      if (t > latest) latest = t;
    }
  }

  // Add 1 unit of padding before and after
  const start = new Date(earliest);
  const end = new Date(latest);
  start.setDate(start.getDate() - 7);
  end.setDate(end.getDate() + 7);

  return { start, end };
}

/** Convert a date to an X pixel coordinate */
export function dateToPixel(
  date: Date,
  timeScale: TimeScale,
  startDate: Date,
  pixelsPerUnit: number,
): number {
  const msPerDay = 86400000;
  const diffDays = (date.getTime() - startDate.getTime()) / msPerDay;

  switch (timeScale) {
    case 'day':
      return diffDays * pixelsPerUnit;
    case 'week':
      return (diffDays / 7) * pixelsPerUnit;
    case 'month':
      return (diffDays / 30) * pixelsPerUnit;
    case 'quarter':
      return (diffDays / 91) * pixelsPerUnit;
  }
}

/** Convert an X pixel coordinate back to a date (inverse of dateToPixel) */
export function pixelToDate(
  x: number,
  timeScale: TimeScale,
  startDate: Date,
  pixelsPerUnit: number,
): Date {
  const msPerDay = 86400000;
  let days: number;

  switch (timeScale) {
    case 'day':
      days = x / pixelsPerUnit;
      break;
    case 'week':
      days = (x / pixelsPerUnit) * 7;
      break;
    case 'month':
      days = (x / pixelsPerUnit) * 30;
      break;
    case 'quarter':
      days = (x / pixelsPerUnit) * 91;
      break;
  }

  const result = new Date(startDate.getTime() + days * msPerDay);
  // Snap to day boundaries
  result.setHours(0, 0, 0, 0);
  return result;
}

/** Generate time intervals for the header axis */
export function getTimeIntervals(
  startDate: Date,
  endDate: Date,
  timeScale: TimeScale,
): TimeInterval[] {
  const intervals: TimeInterval[] = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  switch (timeScale) {
    case 'day':
      while (current <= endDate) {
        intervals.push({
          date: new Date(current),
          label: `${current.getDate()}`,
          sublabel: current.getDate() === 1 ? monthNames[current.getMonth()] : undefined,
        });
        current.setDate(current.getDate() + 1);
      }
      break;

    case 'week': {
      // Align to Monday
      const dayOfWeek = current.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      current.setDate(current.getDate() + diff);
      while (current <= endDate) {
        intervals.push({
          date: new Date(current),
          label: `${monthNames[current.getMonth()]} ${current.getDate()}`,
        });
        current.setDate(current.getDate() + 7);
      }
      break;
    }

    case 'month':
      current.setDate(1);
      while (current <= endDate) {
        intervals.push({
          date: new Date(current),
          label: `${monthNames[current.getMonth()]} ${current.getFullYear()}`,
        });
        current.setMonth(current.getMonth() + 1);
      }
      break;

    case 'quarter':
      current.setDate(1);
      current.setMonth(Math.floor(current.getMonth() / 3) * 3);
      while (current <= endDate) {
        const q = Math.floor(current.getMonth() / 3) + 1;
        intervals.push({
          date: new Date(current),
          label: `Q${q} ${current.getFullYear()}`,
        });
        current.setMonth(current.getMonth() + 3);
      }
      break;
  }

  return intervals;
}

/** Calculate bar X position and width for a task */
export function calculateBarPosition(
  task: GanttTask,
  timeScale: TimeScale,
  startDate: Date,
  pixelsPerUnit: number,
): { x: number; width: number } {
  if (!task.startDate) return { x: 0, width: 0 };

  const x = dateToPixel(task.startDate, timeScale, startDate, pixelsPerUnit);

  if (task.milestone) return { x, width: 0 };

  let endDate = task.endDate;
  if (!endDate && task.duration) {
    endDate = new Date(task.startDate);
    endDate.setDate(endDate.getDate() + task.duration);
  }
  if (!endDate) {
    endDate = new Date(task.startDate);
    endDate.setDate(endDate.getDate() + 1);
  }

  const endX = dateToPixel(endDate, timeScale, startDate, pixelsPerUnit);
  const width = Math.max(endX - x, 2); // Minimum 2px width for visibility

  return { x, width };
}

/** Extend the time range to show enough context for the given time scale */
export function extendRangeForScale(
  range: { start: Date; end: Date },
  timeScale: TimeScale,
): { start: Date; end: Date } {
  const msPerDay = 86400000;
  const rangeDays = (range.end.getTime() - range.start.getTime()) / msPerDay;

  // Minimum visible days per time scale so bars differ across scales
  let minDays: number;
  switch (timeScale) {
    case 'day': minDays = rangeDays; break;
    case 'week': minDays = 56; break;     // ~8 weeks
    case 'month': minDays = 120; break;   // ~4 months
    case 'quarter': minDays = 365; break; // ~4 quarters
  }

  if (minDays <= rangeDays) return range;

  const extraDays = minDays - rangeDays;
  const start = new Date(range.start.getTime() - Math.floor(extraDays / 2) * msPerDay);
  const end = new Date(range.end.getTime() + Math.ceil(extraDays / 2) * msPerDay);

  return { start, end };
}

/** Auto-select the best time scale based on data range */
export function autoSelectTimeScale(rangeInDays: number): TimeScale {
  if (rangeInDays < 14) return 'day';
  if (rangeInDays <= 60) return 'week';
  if (rangeInDays <= 365) return 'month';
  return 'quarter';
}

/** Calculate total SVG width needed for the time range */
export function calculateTotalWidth(
  startDate: Date,
  endDate: Date,
  timeScale: TimeScale,
  pixelsPerUnit: number,
): number {
  const msPerDay = 86400000;
  const diffDays = (endDate.getTime() - startDate.getTime()) / msPerDay;

  switch (timeScale) {
    case 'day': return diffDays * pixelsPerUnit;
    case 'week': return (diffDays / 7) * pixelsPerUnit;
    case 'month': return (diffDays / 30) * pixelsPerUnit;
    case 'quarter': return (diffDays / 91) * pixelsPerUnit;
  }
}
